// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback } from 'react';
import { Stack, Text, Title } from '@mantine/core';

import { observabilityApi } from '../../api/observability';
import type { LogFilter } from '../../api/observability';
import { LogViewer } from './LogViewer';

/**
 * What this Gateway has done.
 *
 * Sessions, the work dispatched to it, transport failures, and the device
 * operations performed through it — the same event stream the Device view
 * reads, asked for by Gateway instead of by device.
 *
 * # WHAT THIS IS NOT
 *
 * Host or system log streaming. The Gateway's own local surface remains setup,
 * status, diagnostics and recovery; the Server is the management console, and
 * this is the Server's record of what the Gateway reported. A Gateway that
 * could stream arbitrary host output would be a Gateway whose logs are an
 * exfiltration channel.
 */
export function GatewayLogs({ gatewayId }: { gatewayId: string }) {
  const load = useCallback(
    (filter: LogFilter) => observabilityApi.gatewayLogs(gatewayId, filter),
    [gatewayId],
  );

  const download = useCallback(() => observabilityApi.downloadGatewayLogs(gatewayId), [gatewayId]);

  return (
    <Stack gap="sm">
      <Title order={4}>Activity</Title>
      <Text size="xs" c="dimmed">
        Connections, dispatched work, transport failures and the device operations
        performed through this Gateway.
      </Text>
      <LogViewer
        load={load}
        download={download}
        emptyMessage="Nothing has been recorded for this Gateway yet."
      />
    </Stack>
  );
}
